// SectionNavigator — the full-screen section launcher (the "Sections" button
// in the top bar). Owns SECTION_GROUPS (the grouped section catalogue), the
// NavButton sub-component, and all its own open/close/pop-out animation state.
// Extracted verbatim from App.js; only external deps are React + APP_STYLES.
// ──────────────────────────────
import React from "react";
import { APP_STYLES } from "../appStyles";


const SECTION_GROUPS = [
  {
    group: "Overview",
    color: "#2563eb",
    icon: "◈",
    sections: [
      { key: "home", label: "Home" },
      { key: "financial", label: "Financial" },
      { key: "projectTracker", label: "Project Tracker" },
      { key: "programme", label: "Programme" },
    ],
  },
  {
    group: "Commercial",
    color: "#7c3aed",
    icon: "◆",
    sections: [
      { key: "certificates", label: "Certificates" },
      { key: "cashflow", label: "Cashflow" },
      { key: "cpa", label: "CPA" },
{ key: "mos", label: "Materials on Site" },
      { key: "roe", label: "ROE" },
      { key: "variations", label: "Variations" },
      { key: "claims", label: "Claims" },
      { key: "timeBars", label: "Time-Bars & Notices" },
      { key: "finalAccount", label: "Final Account" },
    ],
  },
  {
    group: "Technical",
    color: "#0891b2",
    icon: "◉",
    sections: [
      { key: "boq", label: "BOQ" },
      { key: "soqImport", label: "SOQ Import" },
      { key: "soqRegister", label: "SOQ Register" },
      { key: "drawings", label: "Drawings" },
      { key: "contract", label: "Contract" },
    ],
  },
  {
    group: "Site & Admin",
    color: "#059669",
    icon: "◎",
    sections: [
      { key: "rfis", label: "RFIs" },
      { key: "instructions", label: "Instructions" },
      { key: "diary", label: "Diary" },
      { key: "visualProgress", label: "Visual Progress" },
      { key: "quality", label: "Quality Tests" },
      { key: "ncr", label: "NCRs" },
{ key: "meetings", label: "Meetings" },
{ key: "defects", label: "Defects & Snags" },
      { key: "completion", label: "Completion & Defects" },
    ],
  },
  {
    group: "Compliance",
    color: "#d97706",
    icon: "◐",
    sections: [
      { key: "env", label: "Environmental" },
      { key: "hs", label: "Health & Safety" },
      { key: "audit", label: "Audit Trail" },
      { key: "claude", label: "Claude Review" },
      { key: "risk", label: "Risk Register" },
      { key: "templates", label: "Templates" },
    ],
  },
  {
    group: "Admin Tools",
    color: "#dc2626",
    icon: "⚙",
    sections: [
      { key: "projectRegister", label: "Project Register", adminOnly: true },
      { key: "users", label: "User Management", adminOnly: true },
      { key: "clients", label: "Client Management", adminOnly: true },
    ],
  },
];

function SectionNavigator({ activeSection, onNavigate, isAdmin }) {
  const [open, setOpen] = React.useState(false);
  const [visible, setVisible] = React.useState(false);
  const [selectedGroup, setSelectedGroup] = React.useState(null);
  const [panelVisible, setPanelVisible] = React.useState(false);

  // Hide admin-only sections (User/Client Management) from non-admins.
  const groups = React.useMemo(() => (
    isAdmin
      ? SECTION_GROUPS
      : SECTION_GROUPS
          .map((g) => ({ ...g, sections: g.sections.filter((s) => !s.adminOnly) }))
          .filter((g) => g.sections.length > 0)
  ), [isAdmin]);

  const handleOpen = () => {
    const current = groups.find(g =>
      g.sections.some(s => s.key === activeSection)
    );
    setSelectedGroup(current || null);
    setPanelVisible(!!current);
    setOpen(true);
    requestAnimationFrame(() => setTimeout(() => setVisible(true), 10));
  };

  const handleClose = () => {
    setVisible(false);
    setPanelVisible(false);
    setTimeout(() => {
      setOpen(false);
      setSelectedGroup(null);
    }, 240);
  };

  const handleNavigate = (key) => {
    onNavigate(key);
    handleClose();
  };

  const handleSelectGroup = (group) => {
    if (selectedGroup?.group === group.group) {
      setPanelVisible(false);
      setTimeout(() => setSelectedGroup(null), 180);
    } else {
      setPanelVisible(false);
      setTimeout(() => {
        setSelectedGroup(group);
        setPanelVisible(true);
      }, selectedGroup ? 160 : 0);
    }
  };

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      {/* ── Trigger button (unchanged look) ── */}
      <button
        onClick={handleOpen}
        style={APP_STYLES.sectionsButton}
        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.13)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; }}
      >
        <span style={{ fontSize: 15, lineHeight: 1 }}>⊞</span>
        Sections
      </button>

      {/* ── Overlay ── */}
      {open && (
        <div
          onClick={handleClose}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 200,
            backdropFilter: visible ? "blur(10px)" : "blur(0px)",
            WebkitBackdropFilter: visible ? "blur(10px)" : "blur(0px)",
            background: visible ? "rgba(4,8,18,0.88)" : "rgba(4,8,18,0)",
            transition: "all 0.24s ease",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* ── Nav card — stops backdrop click ── */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              display: "flex",
              alignItems: "stretch",
              gap: 0,
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0) scale(1)" : "translateY(12px) scale(0.97)",
              transition: "opacity 0.24s ease, transform 0.24s ease",
              minHeight: 360,
              background: "#0E1B2C",
              backgroundImage: "linear-gradient(180deg, #15263C 0%, #0E1B2C 100%)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 14,
              boxShadow: "0 24px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04) inset, 0 1px 0 rgba(255,255,255,0.06) inset",
              padding: "18px 10px",
            }}
          >
            {/* ── LEFT: category list ── */}
            <div style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              gap: 3,
              width: 220,
              padding: "8px 0",
            }}>
              {/* Eyebrow label */}
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "0 18px",
                marginBottom: 14,
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: 1,
                  background: "#FF6B35",
                  boxShadow: "0 0 8px #FF6B35",
                }} />
                <span style={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.22em",
                  color: "rgba(202,212,228,0.7)",
                  textTransform: "uppercase",
                  fontFamily: '"Inter", sans-serif',
                }}>
                  Navigate
                </span>
              </div>

              {groups.map((group) => {
                const isActive = selectedGroup?.group === group.group;
                return (
                  <button
                    key={group.group}
                    onClick={() => handleSelectGroup(group)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "11px 16px",
                      borderRadius: 8,
                      cursor: "pointer",
                      border: isActive
                        ? `1px solid ${group.color}55`
                        : "1px solid transparent",
                      background: isActive
                        ? `${group.color}1F`
                        : "transparent",
                      transition: "all 0.18s ease",
                      width: "100%",
                      textAlign: "left",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.background = "transparent";
                    }}
                  >
                    {/* Accent dot */}
                    <span style={{
                      width: 8,
                      height: 8,
                      borderRadius: 2,
                      flexShrink: 0,
                      background: group.color,
                      boxShadow: isActive ? `0 0 10px ${group.color}` : "none",
                      transition: "box-shadow 0.18s ease",
                      transform: isActive ? "scale(1.2)" : "scale(1)",
                    }} />
                    {/* Label */}
                    <span style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: isActive ? "#FAFAF7" : "#B0BDD0",
                      transition: "color 0.18s ease",
                      fontFamily: '"Inter", sans-serif',
                    }}>
                      {group.group}
                    </span>
                    {/* Arrow */}
                    <span style={{
                      marginLeft: "auto",
                      fontSize: 14,
                      color: isActive ? group.color : "rgba(176,189,208,0.5)",
                      opacity: isActive ? 1 : 0,
                      transform: isActive ? "translateX(0)" : "translateX(-4px)",
                      transition: "all 0.18s ease",
                    }}>›</span>
                  </button>
                );
              })}
            </div>

            {/* ── Vertical divider ── */}
            <div style={{
              width: 1,
              margin: "20px 20px",
              background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.18) 25%, rgba(255,255,255,0.18) 75%, transparent)",
              flexShrink: 0,
            }} />

            {/* ── RIGHT: sub-items panel ── */}
            <div style={{
              width: 260,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              padding: "8px 0",
            }}>
              {/* Empty state */}
              {!selectedGroup && (
                <div style={{
                  textAlign: "center",
                  opacity: 0.4,
                }}>
                  <div style={{
                    width: 52,
                    height: 52,
                    borderRadius: "50%",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 12px",
                  }}>
                    <img src={`${process.env.PUBLIC_URL}/logo-mark.png`} alt="" style={{ width: 26, height: 26, borderRadius: 7, marginBottom: 2 }} />
                    <span style={{ fontSize: 7, color: "#475569", letterSpacing: "0.15em", textTransform: "uppercase" }}>Nav</span>
                  </div>
                  <p style={{ fontSize: 11, color: "#475569", letterSpacing: "0.06em", margin: 0 }}>
                    Select a group
                  </p>
                </div>
              )}

              {/* Section list */}
              {selectedGroup && (
                <div style={{
                  width: "100%",
                  opacity: panelVisible ? 1 : 0,
                  transform: panelVisible ? "translateX(0)" : "translateX(10px)",
                  transition: "opacity 0.22s ease, transform 0.22s ease",
                }}>
                  {/* Sub-header */}
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 14,
                  }}>
                    <span style={{
                      width: 10,
                      height: 10,
                      borderRadius: 3,
                      background: selectedGroup.color,
                      transform: "rotate(45deg)",
                      flexShrink: 0,
                    }} />
                    <span style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: "rgba(148,163,184,0.85)",
                    }}>
                      {selectedGroup.group}
                    </span>
                  </div>

                  {/* Section buttons */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {selectedGroup.sections.map((section, idx) => {
                      const isActive = activeSection === section.key;
                      const handlePopOut = (e) => {
                        e.stopPropagation();
                        const url = new URL(window.location.href);
                        url.searchParams.set("section", section.key);
                        window.open(url.toString(), `pm_section_${section.key}`, "width=1280,height=900,menubar=no,toolbar=no,location=no,status=no");
                      };
                      return (
                        <div
                          key={section.key}
                          className="nav-section-row"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0,
                            borderRadius: 8,
                            border: isActive
                              ? `1px solid ${selectedGroup.color}55`
                              : "1px solid rgba(255,255,255,0.06)",
                            background: isActive
                              ? `${selectedGroup.color}20`
                              : "rgba(255,255,255,0.03)",
                            transition: "all 0.14s ease",
                            animationDelay: `${idx * 0.04}s`,
                            overflow: "hidden",
                            position: "relative",
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = "rgba(255,255,255,0.09)";
                              e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                            }
                            e.currentTarget.querySelector(".row-actions").style.opacity = "1";
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive) {
                              e.currentTarget.style.background = "rgba(255,255,255,0.03)";
                              e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)";
                            }
                            e.currentTarget.querySelector(".row-actions").style.opacity = "0";
                          }}
                        >
                          {/* Main clickable area */}
                          <button
                            onClick={() => handleNavigate(section.key)}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              padding: "10px 12px",
                              flex: 1,
                              cursor: "pointer",
                              border: "none",
                              background: "transparent",
                              textAlign: "left",
                              minWidth: 0,
                            }}
                          >
                            <span style={{
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              flexShrink: 0,
                              background: isActive ? selectedGroup.color : "rgba(176,189,208,0.55)",
                              boxShadow: isActive ? `0 0 6px ${selectedGroup.color}` : "none",
                              transition: "all 0.14s ease",
                            }} />
                            <span style={{
                              fontSize: 14,
                              fontWeight: isActive ? 700 : 500,
                              color: isActive ? "#f1f5f9" : "#b0bdd0",
                              transition: "color 0.14s ease",
                              letterSpacing: "0.01em",
                              flex: 1,
                              minWidth: 0,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                              {section.label}
                            </span>
                            {isActive && (
                              <span style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: selectedGroup.color,
                                background: `${selectedGroup.color}18`,
                                border: `1px solid ${selectedGroup.color}30`,
                                borderRadius: 20,
                                padding: "2px 7px",
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                              }}>Active</span>
                            )}
                          </button>

                          {/* Action icons — fade in on hover */}
                          <div
                            className="row-actions"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              paddingRight: 8,
                              flexShrink: 0,
                              opacity: 0,
                              transition: "opacity 0.14s ease",
                            }}
                          >
                            {/* Navigate icon */}
                            <button
                              title={`Go to ${section.label}`}
                              onClick={() => handleNavigate(section.key)}
                              style={{
                                width: 26,
                                height: 26,
                                borderRadius: 6,
                                border: "1px solid rgba(255,255,255,0.12)",
                                background: "rgba(255,255,255,0.08)",
                                color: "#94a3b8",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 13,
                                lineHeight: 1,
                                padding: 0,
                                transition: "all 0.14s ease",
                                flexShrink: 0,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = `${selectedGroup.color}33`;
                                e.currentTarget.style.borderColor = `${selectedGroup.color}88`;
                                e.currentTarget.style.color = "#f1f5f9";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                                e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                                e.currentTarget.style.color = "#94a3b8";
                              }}
                            >
                              ↗
                            </button>

                            {/* Pop-out icon */}
                            <button
                              title={`Open ${section.label} in new window`}
                              onClick={handlePopOut}
                              style={{
                                width: 26,
                                height: 26,
                                borderRadius: 6,
                                border: "1px solid rgba(255,255,255,0.12)",
                                background: "rgba(255,255,255,0.08)",
                                color: "#94a3b8",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 12,
                                lineHeight: 1,
                                padding: 0,
                                transition: "all 0.14s ease",
                                flexShrink: 0,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = "rgba(16,185,129,0.22)";
                                e.currentTarget.style.borderColor = "rgba(16,185,129,0.5)";
                                e.currentTarget.style.color = "#6ee7b7";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "rgba(255,255,255,0.08)";
                                e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
                                e.currentTarget.style.color = "#94a3b8";
                              }}
                            >
                              ⧉
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Footer hint ── */}
          <div style={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: 11,
            color: "rgba(255,255,255,0.18)",
            letterSpacing: "0.08em",
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}>
            {selectedGroup
              ? "Select a section — Esc to close"
              : "Select a group to navigate — Esc to close"}
          </div>
        </div>
      )}
    </>
  );
}

function NavButton({ label, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        ...APP_STYLES.navButton,
        ...(isActive ? APP_STYLES.navButtonActive : {}),
      }}
    >
      {label}
    </button>
  );
}

export default SectionNavigator;
